/**
 * `<policy-conditions>` — the CEL condition editor (`SC-F510`).
 *
 * Five kinds carry `spec.conditions`, and every condition is a hard gate: all of
 * them must be true for the policy to apply, and a **runtime error in one denies
 * the entire request** rather than evaluating to false. So this editor's job is
 * not to make CEL pleasant — it is to make the four ways an expression can be
 * silently wrong impossible to miss.
 *
 * # The vocabulary is offered, never typed from memory
 *
 * The variable picker is generated from `GET /v1/policy-schema`'s `celVocabulary`,
 * which is the same list the control-plane linter enforces. That is the whole
 * design: a completion this editor offers is one the save accepts, and a name it
 * does not offer is one the save refuses. There is no literal vocabulary in this
 * file.
 *
 * **`request.*` is absent from the picker.** It is declared by Sluice's own CEL
 * environment and documented on its own page with a copy-pasteable example, and it
 * is dead on the live path (`SLUICE-U12`): nothing populates the request fact map
 * on REST or MCP, while `sluice policy test` DOES populate it. An expression built
 * on it passes CI and behaves differently in production — for the documented reject
 * rule, it rejects EVERY query. That is the same rule the template picker enforces
 * by construction, driven by the same `offered: false` flag, so the two surfaces
 * cannot drift apart. The reason is shown rather than the field silently omitted,
 * because an author who copied the documentation deserves an explanation.
 *
 * # The diagnostics come from the server
 *
 * Findings are drawn inline by `bn-code` from `POST /v1/policy-cel-check`,
 * debounced. There is deliberately **no second implementation of the CEL subset in
 * this bundle**: the value of `SC-F529` is that the finding an author sees is the
 * finding the publish gate enforces, and two implementations would agree until a
 * pin bump moved one of them. The cost is a request per pause in typing; the
 * benefit is that the editor cannot be wrong in a way the gate is not.
 *
 * A clean answer is shown as "no rule matched", never as "this compiles" — the
 * response's own `basis` sentence, rendered rather than paraphrased.
 *
 * All four states: loading (the check in flight), empty (no conditions, with the
 * consequence of adding one stated), error (the `apierr` code AND the request id),
 * populated.
 *
 * Stories: `SC-S523` (the linter blocks publish on every known trap), `SC-S509`
 * (the access gate, whose editor carries conditions), `SC-S513` (the `request.*`
 * absence, which this shares with the template picker).
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../../components/index.js";
import type { BnCodeDiagnostic, BnSelectChangeDetail, BnSelectOption } from "../../components/index.js";
import {
  checkCelExpression,
  type CelEnvironmentName,
  type CelFinding,
  type CelVariableDoc,
  type PolicyFinding,
} from "../../api/policies.js";
import { ApiError, parseError } from "../../api/errors.js";
import type { Condition } from "./model.js";
import type { PolicySchema } from "./schema.js";

/** Detail of the editor's `conditions-change` event. */
export interface ConditionsChangeDetail {
  /** The new list, or undefined when it empties — so the key leaves the rendered
   * document rather than becoming an empty array. */
  conditions: Condition[] | undefined;
}

/** How long to wait after a keystroke before asking the server. 400ms is long
 * enough that typing an expression is one request rather than thirty, and short
 * enough that a pause reads as immediate. */
const CHECK_DEBOUNCE_MS = 400;

/** The per-expression check state. */
interface CheckState {
  findings: CelFinding[];
  basis: string;
  loading: boolean;
  error: ApiError | null;
}

const IDLE: CheckState = { findings: [], basis: "", loading: false, error: null };

@customElement("policy-conditions")
export class PolicyConditions extends LitElement {
  @property({ attribute: false }) schema!: PolicySchema;
  @property({ attribute: false }) conditions: Condition[] = [];
  /** The object's own findings from the last save, so a finding the server
   * anchored to `spec.conditions[i].expression` is drawn on that expression even
   * before the live check runs. */
  @property({ attribute: false }) findings: PolicyFinding[] = [];
  /** Which environment these expressions are compiled in. `condition` for
   * `spec.conditions` and a reject rule; `row-filter` for
   * `spec.filter.expression`, whose subset is much smaller. */
  @property() environment: CelEnvironmentName = "condition";
  /** True when this surface may only display. An imported object on a read-only
   * page still has to be readable. */
  @property({ type: Boolean }) readonly = false;

  /** Per-index check state, keyed by position in the list. */
  @state() private checks: Record<number, CheckState> = {};
  /** Which condition's variable picker is open. */
  @state() private picker = -1;

  private timers: Record<number, ReturnType<typeof setTimeout>> = {};
  private seq = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    h3 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede,
    .sub {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 82ch;
    }
    .sub {
      margin: var(--bino-space-sm) 0 0;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-md);
    }
    .item {
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
      padding: var(--bino-space-md);
      display: grid;
      gap: var(--bino-space-sm);
    }
    .item-head {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .item-head bn-field {
      margin-bottom: 0;
      flex: 1 1 14rem;
      min-width: 0;
    }
    .spacer {
      flex: 1 1 auto;
    }
    .code {
      min-height: 5rem;
    }
    .status {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .basis {
      margin: 0;
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-muted);
      max-width: 82ch;
    }
    .picker {
      border-top: 1px solid var(--bino-border-light);
      padding-top: var(--bino-space-sm);
      display: grid;
      gap: var(--bino-space-sm);
    }
    .unavailable {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-sm);
      max-width: 82ch;
    }
    .error-message {
      margin: 0;
      color: var(--bino-error);
      font-size: var(--bino-font-size-sm);
    }
    code {
      font-family: var(--bino-font-mono);
    }
    .contract {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text);
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-sm);
      max-width: 82ch;
    }
  `;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    for (const t of Object.values(this.timers)) clearTimeout(t);
  }

  private emit(conditions: Condition[]) {
    this.dispatchEvent(
      new CustomEvent<ConditionsChangeDetail>("conditions-change", {
        detail: { conditions: conditions.length === 0 ? undefined : conditions },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private patch(index: number, patch: Partial<Condition>) {
    const next = this.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c));
    this.emit(next);
    if (patch.expression !== undefined) this.scheduleCheck(index, patch.expression);
  }

  private add() {
    // A new condition starts EMPTY rather than with a plausible expression. An
    // empty expression is a compile error upstream, which the check reports
    // immediately — and that is the honest first thing an author should see about
    // this field, rather than a template they might publish unread.
    this.emit([...this.conditions, { name: "", expression: "" }]);
  }

  private removeAt(index: number) {
    this.emit(this.conditions.filter((_, i) => i !== index));
    const { [index]: _dropped, ...rest } = this.checks;
    this.checks = rest;
  }

  /** scheduleCheck debounces one expression's server check. */
  private scheduleCheck(index: number, expression: string) {
    if (this.timers[index]) clearTimeout(this.timers[index]);
    this.timers[index] = setTimeout(() => void this.runCheck(index, expression), CHECK_DEBOUNCE_MS);
  }

  private async runCheck(index: number, expression: string) {
    const mine = ++this.seq;
    this.setCheck(index, { ...(this.checks[index] ?? IDLE), loading: true, error: null });
    try {
      const res = await checkCelExpression(expression, this.environment);
      if (mine !== this.seq) return;
      this.setCheck(index, {
        findings: res.findings,
        basis: res.basis,
        loading: false,
        error: null,
      });
    } catch (e) {
      if (mine !== this.seq) return;
      // A failed check is reported and never silently treated as an all-clear: an
      // author must not be able to read a network failure as "the expression is
      // fine".
      this.setCheck(index, { findings: [], basis: "", loading: false, error: parseError(e) });
    }
  }

  private setCheck(index: number, state: CheckState) {
    this.checks = { ...this.checks, [index]: state };
  }

  /**
   * diagnosticsFor merges the server's live check with the findings the last save
   * returned.
   *
   * Both are the same rule set — the live route and the save both run
   * `policymodel`'s registry — so the merge is by rule id and the live answer
   * wins: it is about the text currently in the editor, while a saved finding may
   * be about text the author has since changed. A saved finding whose rule the
   * live check does not report is still shown, because the save-time pass sees the
   * whole object and the expression check sees one expression.
   */
  private diagnosticsFor(index: number, expression: string): BnCodeDiagnostic[] {
    const live = this.checks[index]?.findings ?? [];
    const out: BnCodeDiagnostic[] = live.map((f) => ({
      from: f.from,
      to: f.to,
      severity: f.severity,
      message: f.message,
      ruleId: f.rule,
    }));
    const liveRules = new Set(live.map((f) => f.rule));
    const path = `spec.conditions[${index}].expression`;
    for (const f of this.findings) {
      if (f.path !== path || liveRules.has(f.rule)) continue;
      out.push({
        from: 0,
        to: expression.length,
        severity: f.severity,
        message: f.message,
        ruleId: f.rule,
      });
    }
    return out;
  }

  /** The variable-picker options for the offered roots, flattened to paths. */
  private pickerOptions(): BnSelectOption[] {
    const env = this.schema?.celEnvironment(this.environment);
    const out: BnSelectOption[] = [];
    for (const v of env?.variables ?? []) {
      if (!v.offered) continue;
      if (v.openMap) {
        out.push({
          value: `${v.name}.`,
          label: `${v.name}.<key>`,
          description: v.help ?? "",
        });
        continue;
      }
      for (const f of v.fields ?? []) {
        const path = `${v.name}.${f.name}`;
        out.push({
          value: f.openMap ? `${path}.` : path,
          label: f.openMap ? `${path}.<key>` : path,
          description: [f.type, f.help].filter(Boolean).join(" — "),
        });
      }
    }
    return out;
  }

  /** insert appends a picked path at the end of the expression. It appends rather
   * than replacing the selection because `bn-code` owns the caret and reaching into
   * it would be a second editor state to keep in sync. */
  private insert(index: number, path: string) {
    const current = this.conditions[index]?.expression ?? "";
    const joiner = current === "" || current.endsWith(" ") || current.endsWith("(") ? "" : " ";
    this.patch(index, { expression: current + joiner + path });
  }

  /** The unoffered variables, rendered as the explanation the picker owes an
   * author who came looking for them. */
  private renderUnavailable(): TemplateResult | typeof nothing {
    const env = this.schema?.celEnvironment(this.environment);
    const hidden = (env?.variables ?? []).filter((v) => !v.offered);
    if (hidden.length === 0) return nothing;
    return html`${hidden.map(
      (v) => html`<p class="unavailable" role="note">
        <strong><code>${v.name}.*</code> is not offered.</strong> ${v.reason ?? ""}
        ${v.upstream ? html` Tracked as ${v.upstream}.` : nothing} The linter refuses an expression
        that reads it, so this is not a preference — it is a shape that cannot be published.
      </p>`,
    )}`;
  }

  private renderVariableRows(v: CelVariableDoc): TemplateResult {
    return html`<bn-kv
      .rows=${(v.fields ?? []).map((f) => ({
        label: `${v.name}.${f.name}`,
        value: [f.type, f.help].filter(Boolean).join(" — "),
      }))}
    ></bn-kv>`;
  }

  private renderPicker(index: number): TemplateResult {
    const env = this.schema?.celEnvironment(this.environment);
    const offered = (env?.variables ?? []).filter((v) => v.offered && !v.openMap);
    return html`<div class="picker">
      <bn-field
        label="Insert a variable"
        hint="Only the variables and fields this build's CEL environment actually populates are offered. A field outside this list type-checks and then errors at request time, which denies the whole request."
      >
        <bn-select
          filterable
          .options=${this.pickerOptions()}
          .value=${""}
          placeholder="Pick a variable…"
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            e.stopPropagation();
            if (e.detail.value) this.insert(index, e.detail.value);
          }}
        ></bn-select>
      </bn-field>
      ${offered.map((v) => this.renderVariableRows(v))} ${this.renderUnavailable()}
    </div>`;
  }

  private renderStatus(index: number): TemplateResult {
    const state = this.checks[index] ?? IDLE;
    if (state.error) {
      return html`<div class="status" role="alert">
        <p class="error-message">The expression could not be checked: ${state.error.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: state.error.code },
            ...(state.error.requestId ? [{ label: "Request id", value: state.error.requestId }] : []),
          ]}
        ></bn-kv>
        <bn-button
          size="sm"
          @click=${() => void this.runCheck(index, this.conditions[index]?.expression ?? "")}
        >
          Check again
        </bn-button>
      </div>`;
    }
    if (state.loading) {
      return html`<div class="status" role="status" aria-live="polite">
        <bn-spinner size="14" label="Checking the expression"></bn-spinner>
        <span>Checking…</span>
      </div>`;
    }
    const errors = state.findings.filter((f) => f.severity === "error").length;
    const warnings = state.findings.length - errors;
    return html`<div class="status" role="status" aria-live="polite">
      ${state.basis === ""
        ? html`<span>Not checked yet — the expression is checked as you pause typing.</span>`
        : errors + warnings === 0
          ? html`<bn-badge variant="success">No rule matched</bn-badge>`
          : html`${errors > 0
              ? html`<bn-badge variant="error">${errors} error${errors === 1 ? "" : "s"}</bn-badge>`
              : nothing}
            ${warnings > 0
              ? html`<bn-badge variant="warning"
                  >${warnings} warning${warnings === 1 ? "" : "s"}</bn-badge
                >`
              : nothing}`}
    </div>
    ${state.basis ? html`<p class="basis">${state.basis}</p>` : nothing}`;
  }

  private renderCondition(c: Condition, index: number): TemplateResult {
    const expression = c.expression ?? "";
    return html`<div class="item">
      <div class="item-head">
        <bn-field
          label="Name"
          hint="Required by us and not upstream: it is what appears in the error a client sees and in a policy test assertion."
        >
          <bn-input
            mono
            .value=${c.name ?? ""}
            placeholder="gold-tier"
            ?disabled=${this.readonly}
            @input=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              this.patch(index, { name: e.detail.value });
            }}
          ></bn-input>
        </bn-field>
        <span class="spacer"></span>
        ${this.readonly
          ? nothing
          : html`<bn-button
                size="sm"
                variant="ghost"
                aria-expanded=${this.picker === index ? "true" : "false"}
                @click=${() => (this.picker = this.picker === index ? -1 : index)}
              >
                ${this.picker === index ? "Hide variables" : "Variables"}
              </bn-button>
              <bn-button
                size="sm"
                variant="ghost"
                aria-label=${`Remove condition ${c.name || index + 1}`}
                @click=${() => this.removeAt(index)}
              >
                Remove
              </bn-button>`}
      </div>
      <div class="code">
        <bn-code
          language="cel"
          hide-toolbar
          hide-line-numbers
          label=${`Condition ${c.name || index + 1}`}
          .readonly=${this.readonly}
          .value=${expression}
          .diagnostics=${this.diagnosticsFor(index, expression)}
          @bn-code-change=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.patch(index, { expression: e.detail.value });
          }}
        ></bn-code>
      </div>
      ${this.renderStatus(index)} ${this.picker === index ? this.renderPicker(index) : nothing}
    </div>`;
  }

  override render() {
    if (!this.schema) return nothing;
    const env = this.schema.celEnvironment(this.environment);
    const list = this.conditions ?? [];
    return html`<section>
      <h3>Conditions</h3>
      <p class="lede">
        ${this.schema.groupHelp("condition") ||
        "Every condition must be true for this policy to apply."}
      </p>
      ${env?.help ? html`<p class="contract">${env.help}</p>` : nothing}
      ${list.length === 0
        ? html`<bn-empty-state
            icon="ƒ"
            heading="No condition"
            message="This policy applies whenever its matcher matches. A condition narrows it further and is a hard gate: all conditions must be true, and a runtime error in one denies the whole request rather than evaluating to false. Most policies need none — the matcher covers subject and resource, and a condition is for the query shape and claim tests it cannot express."
          ></bn-empty-state>`
        : html`<div class="stack">${list.map((c, i) => this.renderCondition(c, i))}</div>`}
      ${this.readonly
        ? nothing
        : html`<p class="sub">
            <bn-button size="sm" @click=${() => this.add()}>Add a condition</bn-button>
          </p>`}
      ${list.length > 0
        ? html`<p class="sub">
            Any policy carrying a condition makes the rewrite cache key on the full header set, so a
            condition has a throughput cost as well as a decision effect.
          </p>`
        : nothing}
      ${list.length === 0 ? this.renderUnavailable() : nothing}
    </section>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-conditions": PolicyConditions;
  }
  interface HTMLElementEventMap {
    "conditions-change": CustomEvent<ConditionsChangeDetail>;
  }
}
